import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Safely convert Firestore timestamp to Date
 * Handles both Firestore Timestamp objects and plain objects from REST API
 */
const toDate = (timestamp) => {
  if (!timestamp) return null;

  // If it's already a Date
  if (timestamp instanceof Date) return timestamp;

  // If it has toDate method (Firestore Timestamp)
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }

  // If it's a plain object with _seconds (from REST API)
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }

  // If it's an ISO string
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }

  return null;
};

export default function InvitationsTab({
  invitations,
  onInvite,
  onCancel,
  canInvite = false
}) {
  const { t } = useTranslation('organization');

  return (
    <div className="invitations-tab">
      <div className="tab-header">
        <h2>{t('invitations.title')}</h2>
        {canInvite && (
          <button className="btn-primary" onClick={onInvite}>
            {t('invitations.inviteButtonIcon')}
          </button>
        )}
      </div>

      {invitations.length === 0 ? (
        <div className="empty-state">
          <h3>{t('invitations.emptyTitle')}</h3>
          {canInvite ? (
            <>
              <p>{t('invitations.emptyDescription')}</p>
              <button className="btn-primary" onClick={onInvite}>
                {t('invitations.inviteButton')}
              </button>
            </>
          ) : (
            <p>{t('invitations.noPermissionsToInvite')}</p>
          )}
        </div>
      ) : (
        <table className="invitations-table">
          <thead>
            <tr>
              <th>{t('invitations.headerEmail')}</th>
              <th>{t('invitations.headerRole')}</th>
              <th>{t('invitations.headerInvitedBy')}</th>
              <th>{t('invitations.headerDate')}</th>
              <th>{t('invitations.headerExpires')}</th>
              <th>{t('invitations.headerActions')}</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((invite) => (
              <tr key={invite.id}>
                <td>{invite.email}</td>
                <td>
                  <span className={`role-badge ${invite.role}`}>
                    {invite.role}
                  </span>
                </td>
                <td>{invite.invitedByName || invite.invitedByEmail}</td>
                <td>
                  {toDate(invite.createdAt)?.toLocaleDateString() || 'N/A'}
                </td>
                <td>
                  {toDate(invite.expiresAt)?.toLocaleDateString() || 'N/A'}
                </td>
                <td>
                  {canInvite && (
                    <button
                      className="btn-danger btn-sm"
                      onClick={() => onCancel(invite.id)}
                    >
                      {t('invitations.cancel')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!canInvite && invitations.length > 0 && (
        <p className="info-message">
          {t('invitations.noPermissions')}
        </p>
      )}
    </div>
  );
}
